import React, { useEffect } from 'react';
import { useVoiceRecognition } from '../../hooks/useVoiceRecognition';
import './VoiceInput.css';

/**
 * VoiceInput component - Microphone button for voice-to-text
 *
 * Props:
 * - onTranscript: Callback when transcription completes (receives text)
 * - disabled: Whether the mic button is disabled
 */
export function VoiceInput({ onTranscript, disabled }) {
  const {
    transcript,
    isListening,
    isSupported,
    error,
    startListening,
    stopListening,
  } = useVoiceRecognition();

  // Call callback whenever transcript updates (live preview)
  useEffect(() => {
    if (transcript) {
      onTranscript(transcript, isListening); // Pass isListening to indicate interim vs final
    }
  }, [transcript, onTranscript, isListening]);

  const handleClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Don't render if browser doesn't support it
  if (!isSupported) {
    return (
      <div className="voice-input-unsupported" title="Voice input not supported in this browser">
        <button type="button" disabled className="voice-button">
          🎤
        </button>
      </div>
    );
  }

  return (
    <div className="voice-input">
      <button
        type="button"
        className={`voice-button ${isListening ? 'listening' : ''} ${error ? 'error' : ''}`}
        onClick={handleClick}
        disabled={disabled}
        title={
          isListening
            ? 'Stop recording'
            : error
            ? error
            : 'Click to record voice message'
        }
        aria-label={isListening ? 'Stop recording' : 'Start voice recording'}
      >
        {isListening ? '⏹' : '🎤'}
      </button>
      {error && <div className="voice-error-tooltip">{error}</div>}
    </div>
  );
}
